import { BASE_URL } from 'api';
import CAPropertyStockListItemDTO, { getLocalePunchline } from 'common/dto/CAPropertyStockListItemDTO';
import useComponentDidMount from 'common/hooks/useComponentDidMount';
import useSimpleFormBinder, { useLocalBinder } from 'common/hooks/useFormBinder';
import useLayoutProps from 'common/hooks/useLayoutProps';
import useLocale from 'common/hooks/useLocale';
import usePrevious from 'common/hooks/usePrevious';
import { jumpers, useJump } from 'common/theme/jumper';
import { isNonEmpty, multiLang, priceFromView, priceToView } from 'common/utils/utils';
import { PropertyResultMapListItemProps } from 'components/property/search/PropertyResultMapListItem';
import SearchScreenDesktop from 'components/property/search/SearchScreenDesktop';
import { getSearchTitle } from 'containers/home/AdvancedSearchPage';
import deepEqual from 'deep-equal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { IRootState } from 'reducers';
import { alertConfirmDialogActions } from 'reducers/alert-confirm-dialog';
import { useCommonFormReducer } from 'reducers/common/form';
import { enquiryDialogActions } from 'reducers/enquiry-dialog';
import { homeActions } from 'reducers/home';
import { propertyListActions } from 'reducers/property-list';
import { propertyMapActions } from 'reducers/property-map';
import { saveSearchActions } from 'reducers/save-search';
import { format } from 'util';
import { getMergedProperties } from './PropertySearchListingPage';

const EMPTY_ARRAY: any[] = [];

export default function PropertySearchDesktopPage() {
  const pathName = useLocation().pathname.toLowerCase();
  
  const form = useCommonFormReducer(
    (state: IRootState) => state.propertyList,
    propertyListActions.edit,
    propertyListActions.addFormError,
    []
  );

  const layoutProps = useLayoutProps();
  const dispatch = useDispatch();
  const jump = useJump();
  const history = useHistory();

  const { loggedIn } = useSelector((state: IRootState) => state.login);

  const localeState = useLocale();
  const { locale,
    lang, langProperty,
    usageOptions, facingOptions, decoOptions, viewOptions, otherFeatureOptions, primarySchoolNetOptions,
    secondarySchoolNetOptions, districtHkiOptions, districtKltOptions, districtNtOptions,
    otherOptions, clubHouseFacilitiesOptions, levelOptions,
  } = localeState;


  const { soldLeasedProperties, contents: searchForm } = useSelector((state: IRootState) => state.propertyList);
  const mapFiltered = useSelector((state: IRootState) => state.propertyMap.filtered);
  const mapPoints = useSelector((state: IRootState) => state.propertyMap.points);
  const { filteredLoading, filteredHasMore } = useSelector((state: IRootState) => state.propertyMap);
  const { DISPLAY_FIRST_SOLD_LEASED_PROPERTY_AFTER_N_PROPERTIES, 
    DISPLAY_REST_SOLD_LEASED_PROPERTIES_AFTER_N_PROPERTIES
  } = useSelector((state: IRootState) => state.systemSettings.ClientApp.PROPERTY_STOCK);
  const { openSavedSearchCriteriasDialog } = useSelector((state: IRootState) => state.saveSearch);

  const mapItems = useMemo(() => mapPoints.filter(p => p.lat && p.lng).map(p => ({
    id: p.caPropertyStockId ?? (p as any).propertyStockId,
    lat: p.lat,
    lng: p.lng,
    district: p.district,
  })), [ mapPoints ]);

  const SEODistrictMapping = {
    districtHkiOptions: {
      central: "HC", 中環: "HC", 中环: "HC",
      aberdeen: "HA", 香港仔: "HA",
      apleichau: "HA", 鴨脷洲: "HA", 鸭脷洲: "HA",
      causewaybay: "HCB", 銅鑼灣: "HCB", 铜锣湾: "HCB",
      chaiwan: "HCW", 柴灣: "HCW", 柴湾: "HCW",
      happyvalley: "HHV", 跑馬地: "HHV", 跑马地: "HHV",
      kennedytown: "HKT", 堅尼地城: "HKT", 坚尼地城: "HKT",
      western: "HKT", 西區: "HKT", 西区: "HKT",
      midlevelscentral: "HMC", 中半山: "HMC",
      midlevelseast: "HME", 東半山: "HME", 东半山: "HME",
      midlevelswest: "HMW", 西半山: "HMW",
      northpointhill: "HNH", 北角半山: "HNH",
      northpoint: "HNP", 北角: "HNP",
      peak: "HP", 山頂: "HP", 山顶: "HP",
      pokfulam: "HPF", 薄扶林: "HPF", 
      quarrybay: "HQB", 鰂魚涌: "HQB", 鲗鱼涌: "HQB",
      repulsebay: "HRB", 淺水灣: "HRB", 浅水湾: "HRB",
      shousonhill: "HRB", 壽臣山: "HRB", 寿臣山: "HRB",
      shaukeiwan: "HSK", 筲箕灣: "HSK", 筲箕湾: "HSK",
      stanley: "HSL", 赤柱: "HSL",
      siusaiwan: "HSS", 小西灣: "HSS", 小西湾: "HSS",
      sheungwan: "HSW", 上環: "HSW", 上环: "HSW",
      saiyingpun: "HSY", 西營盤: "HSY", 西营盘: "HSY",
      taitam: "HTT", 大潭: "HTT",
      wanchai: "HWC", 灣仔: "HWC", 湾仔: "HWC",
      wongchukhang: "HWH", 黃竹坑: "HWH", 黄竹坑: "HWH"
    },
    districtKltOptions:{
      cheungshawan: "KCS", 長沙灣: "KCS", 长沙湾: "KCS",
      diamondhill: "KDH", 鑽石山: "KDH", 钻石山: "KDH",
      hunghom: "KHH", 紅磡: "KHH", 红磡: "KHH",
      homantin: "KHM", 何文田: "KHM",
      kowloonbay: "KKB", 九龍灣: "KKB", 九龙湾: "KKB",
      kowlooncity: "KKC", 九龍城: "KKC", 九龙城: "KKC",
      kowloontong: "KKL", 九龍塘: "KKL", 九龙塘: "KKL",
      kwuntong: "KKT", 觀塘: "KKT", 观塘: "KKT",
      laichikok: "KLC", 荔枝角: "KLC",
      meifu: "KLC", 美孚: "KLC",
      lamtin: "KLT", 藍田: "KLT", 蓝田: "KLT",
      leiyuemun: "KLT", 鯉魚門: "KLT", 鲤鱼门: "KLT",
      mongkok: "KMK", 旺角: "KMK",
      ngauchaiwan: "KNC", 牛池灣: "KNC", 牛池湾: "KNC",
      ngautaukok: "KNT", 牛頭角: "KNT", 牛头角: "KNT",
      shekkipmei: "KSK", 石硤尾: "KSK", 石硖尾: "KSK",
      sanpokong: "KSP", 新蒲崗: "KSP", 新蒲岗: "KSP",
      shamshuipo: "KSS", 深水埗: "KSS",
      taikoktsui: "KTK", 大角咀: "KTK",
      tsimshatsui: "KTS", 尖沙咀: "KTS",
      tokwawan: "KTW", 土瓜灣: "KTW", 土瓜湾: "KTW",
      wangtauhom: "KWH", 橫頭磡: "KWH", 横头磡: "KWH",
      wongtaisin: "KWT", 黃大仙: "KWT", 黄大仙: "KWT",
      yaumatei: "KYM", 油麻地: "KYM", 
      yautong: "KYT", 油塘: "KYT"
    },
    districtNtOptions:{
      fanling: "NFL", 粉嶺: "NFL", 粉岭: "NFL",
      islands: "NIS", 離島: "NIS", 离岛: "NIS",
      kwaichung: "NKC", 葵涌: "NKC",
      maonshan: "NMO", 馬鞍山: "NMO", 马鞍山: "NMO",
      mawan: "NMW", 馬灣: "NMW", 马湾: "NMW",
      saikung: "NSK", 西貢: "NSK", 西贡: "NSK",
      sheungshui: "NSS", 上水: "NSS",
      shatin: "NST", 沙田: "NST",
      tungchung: "NTC", 東涌: "NTC", 东涌: "NTC",
      tseungkwano: "NTK", 將軍澳: "NTK", 将军澳: "NTK",
      tuenmun: "NTM", 屯門: "NTM", 屯门: "NTM",
      taipo: "NTP", 大埔: "NTP",
      tsuenwan: "NTW", 荃灣: "NTW", 荃湾: "NTW",
      tsingyi: "NTY", 青衣: "NTY",
      yuenlong: "NYL", 元朗: "NYL"
   }
  };

  const getDisplayNameByCode = (code: string): string | null => {
    return (districtHkiOptions[code] || districtKltOptions[code] || districtNtOptions[code] || null);
  };


  const allDistrictOptions = {
    ...SEODistrictMapping.districtHkiOptions,
    ...SEODistrictMapping.districtKltOptions,
    ...SEODistrictMapping.districtNtOptions
  };

  // On Init...
  useComponentDidMount(() => {
    if (mapFiltered.length === 0) {
      // If property list are empty, try fetch once
      dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: false }));
    }
    
    Object.entries(allDistrictOptions).forEach(([seoKey, districtCode]) => {
      if (pathName === `/map-list/${seoKey}`) {
        const districtName = getDisplayNameByCode(districtCode);
        if (districtName) {
          form.field('freeTextSearch').change(districtName);
          dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: false }));
        }
      }
    });



    dispatch(propertyMapActions.fetchPoints());
  });

  useEffect(() => {
    if (!openSavedSearchCriteriasDialog) {
      dispatch(propertyMapActions.fetchPoints());
      dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: false }));
    }
  }, [ openSavedSearchCriteriasDialog ]);

  useEffect(() => {
    // console.log("print fetchFiltered: on mapPoints change");
    dispatch(propertyMapActions.fetchFiltered({
      pidList: mapPoints.map(p => p.caPropertyStockId),
      isSwitchingPage: false,
    }));
  }, [ mapPoints ]);
  
  //Add useLocation for monitoring the entries from homePage's tags
  let location = useLocation();
  const searchFormRef = useRef(searchForm);

  // Update the values of searchForm for comparing the init data
  useEffect(()=>{
    searchFormRef.current = searchForm;
  }, [searchForm]); 

  //Reset the search criteria when entered from homepage's tags and the search criteria have changed
  useEffect(() => {
    return () => {
      // Note: `searchForm` here stands for the initial search data when the page is first rendered.
      if ((location.state as any)?.fromTag && deepEqual(searchFormRef.current, searchForm)){
        dispatch(propertyListActions.reset());
      }
    };
  }, []);
  
  const districtOptions = { ...districtHkiOptions, ...districtKltOptions, ...districtNtOptions };

  const propertyPropsMap = useMemo(() => (property: CAPropertyStockListItemDTO, soldLeased = false) => ({
    id: property.caPropertyStockId,
    propertyNo: property.propertyNo,
    label: `${multiLang(locale, property.streetZh, property.streetEn, property.streetSc) ?? ''} ${multiLang(locale, property.buildingNameZh, property.buildingNameEn, property.buildingNameSc) ?? ''} ${levelOptions[property.level] ?? ''}`,
    street: multiLang(locale, property.streetZh, property.streetEn, property.streetSc) ?? '',
    buildingName: multiLang(locale, property.buildingNameZh, property.buildingNameEn, property.buildingNameSc) ?? '',
    level: levelOptions[property.level] ?? '',
    image: property.photoFilename ? `${BASE_URL}/files/${property.photoFilename}` : '' , // property.image, 
    net: property.net,
    gross: property.gross,
    district: districtOptions[property.district], //
    price: property.price,
    rent: property.rent,
    status: property.status,
    bookmarked: property.isBookmarked,
    isHotpick: property.isHotPick,
    punchline: getLocalePunchline(property, locale),

    onBookmarkClick: () => {
      if (!loggedIn) {
        jump(jumpers.toLogin());
        return;
      }
      property.isBookmarked ? 
        dispatch(propertyListActions.deleteBookmark({ caPropertyStockId: property.caPropertyStockId })) :
        dispatch(propertyListActions.addBookmark({ caPropertyStockId: property.caPropertyStockId }))
      ;
    },

    onClick: () => {
      if (soldLeased) {
        dispatch(alertConfirmDialogActions.alert(
          '',
          property.status === 'SOLD' ? langProperty.msgSoldClicked : langProperty.msgLeasedClicked,
          lang.actionGoBack, ''
        ));
      } else {
        // FIXME: Different route
        window.open(`properties/${property.propertyNo}`);
      }
    },
  }), [ locale, loggedIn ]);

  const [mergedPropertiesProps, setMergedPropertiesProps] = useState<PropertyResultMapListItemProps[]>([]);

  useEffect(() => {
    const merged = getMergedProperties(
      mapFiltered, soldLeasedProperties,
      propertyPropsMap,
      DISPLAY_FIRST_SOLD_LEASED_PROPERTY_AFTER_N_PROPERTIES, DISPLAY_REST_SOLD_LEASED_PROPERTIES_AFTER_N_PROPERTIES
    );
    setMergedPropertiesProps(merged);
  }, [ mapFiltered ?? EMPTY_ARRAY, soldLeasedProperties ?? EMPTY_ARRAY, propertyPropsMap ]);

  const { saveSearchTitle, saveSearchTitleErrorMsg, openSaveDialog } = useSelector((state: IRootState) => state.saveSearch);
  const maxTitleLength = 16777215;

  const bindSearchTitle = useSimpleFormBinder(
		saveSearchTitle,
		(updated) => dispatch(saveSearchActions.updateSaveSearchTitle(updated ?? '')),
		() => { dispatch(saveSearchActions.updateSaveSearchTitleError(isNonEmpty(saveSearchTitle) ? '' : langProperty.errPleaseEnterTitle)); },
		saveSearchTitleErrorMsg
  );

  const mapPidList = useLocalBinder<number[]>([]);

  const prevLocale = usePrevious<string>(locale);

	useEffect(() => {
    if (prevLocale !== locale) {
      dispatch(propertyListActions.edit("priceDisplay", 
      form.field("priceDisplay").value?.map(v => priceToView(priceFromView(v, prevLocale ?? locale), locale)))
      );
    }
  }, [ locale ]);

  useEffect(() => {
    dispatch(propertyMapActions.fetchPoints());
    dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: false }));
    dispatch(homeActions.updateSearchType(form.field('buyOrRent').value=='RENT'?'RENT':'BUY'));
  }, [ form.field('buyOrRent').value ]);
  
  return <SearchScreenDesktop
    layoutProps={layoutProps}
    searchScreenProps={{
      bindSearchTitle,
      form: form,

      saveDialogOpen: openSaveDialog,

      usageOptions,
      facingOptions,
      decoOptions,
      viewOptions,
      otherFeatureOptions,
      primarySchoolNetOptions,
      secondarySchoolNetOptions,
      districtHkiOptions,
      districtKltOptions,
      districtNtOptions,
      clubHouseFacilitiesOptions,
      otherOptions,

      onSearchClick: () => {
        dispatch(propertyMapActions.fetchPoints());
        // console.log("print fetchFiltered: onSearchClick");
        dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: false }));
        form.field('freeTextSearch').change(form.field('freeTextSearch').value?.trim());
        form.field('street').change(form.field('street').value?.trim());
        form.field('buildingName').change(form.field('buildingName').value?.trim());
      },
      onSaveClick: () => {
        if (!loggedIn) {
          jump(jumpers.toLogin());
        } else {
          form.field('freeTextSearch').change(form.field('freeTextSearch').value?.trim());
          form.field('street').change(form.field('street').value?.trim());
          form.field('buildingName').change(form.field('buildingName').value?.trim());
          dispatch(saveSearchActions.updateSaveSearchTitle(getSearchTitle(searchForm, localeState)));
          dispatch(saveSearchActions.openSaveSearchDialog());
        }
      },
      onSaveSubmit: () => {
        if (!isNonEmpty(bindSearchTitle.value)) {
          dispatch(saveSearchActions.updateSaveSearchTitleError(langProperty.errPleaseEnterTitle));
        }
        else if (bindSearchTitle.value!.length > maxTitleLength) {
          dispatch(saveSearchActions.updateSaveSearchTitleError(format(lang.msgMaxLength, maxTitleLength)));
        }
        else {
          dispatch(saveSearchActions.addSearchCriteria());
          dispatch(saveSearchActions.closeSaveSearchDialog());
          dispatch({ type: 'Layout.AlertMessageAdded', payload: { message: langProperty.msgSaveSearchSuccess, severity: "success" } });
        }
      },
      onHideSaveDialog: () => {
        dispatch(saveSearchActions.closeSaveSearchDialog());
      },
      onRequestMore: () => {
        dispatch(propertyMapActions.fetchFiltered({ pidList: mapPidList.value ?? [], isSwitchingPage: true }))
      },
      onResetClick: () => {
        dispatch(propertyListActions.reset());
        dispatch(propertyMapActions.fetchPoints());
        dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: true }));
      },
    }}

    ordering={{
      direction: searchForm.direction ?? 'DESC',
      sortKey: searchForm.sortKey ?? null,
      onSortSelect: (newSort, newDirection) => {
        dispatch(propertyListActions.edit('sortKey', newSort));
        dispatch(propertyListActions.edit('direction', newDirection));
        // console.log("print fetchFiltered: ordering");
        dispatch(propertyMapActions.fetchFiltered({ pidList: mapPidList.value ?? [], isSwitchingPage: false }));
      }
    }}

    mapViewProps={{
      center: { lat: 22.302711, lng: 114.177216 },
      actualViewHeightPct: 1,
      properties: mapItems,
      onClusterClick: (pidList) => {
        mapPidList.change(pidList);
        dispatch(propertyMapActions.fetchFiltered({ pidList, isSwitchingPage: false }));
      },
      onClusteredPidListChange: (pidList) => {
        mapPidList.change(pidList);
        // console.log("print fetchFiltered: on clusterPidList change");
        dispatch(propertyMapActions.fetchFiltered({ pidList, isSwitchingPage: false }));
      },
      onResetClick: () => {
        dispatch(propertyListActions.reset());
        dispatch(propertyMapActions.fetchPoints());
        dispatch(propertyMapActions.fetchFiltered({ pidList: [], isSwitchingPage: true }));
      },
    }}

    mapResultProps={{
      layoutProps,
      center: { lat: 22.302711, lng: 114.177216 },
      properties: mergedPropertiesProps,
      filteredLoading,
      filteredHasMore,
      propertyPoints: mapPoints.filter(p => p.lat && p.lng).map(p => ({
        id: p.caPropertyStockId ?? (p as any).propertyStockId,
        lat: p.lat,
        lng: p.lng,
        district: p.district,
      })),
    }}

    onEnquiryClick={() => dispatch(enquiryDialogActions.openDialog())}
  />
}